/**
 * «Распределение»: вкладки `?kind=` — лиды (по умолчанию) и сделки обзвона
 * (`DealsTab`, спека `docs/specs/2026-09-25-deal-distribution-design.md`).
 * Лиды NEW — сколько не распределено (на системной учётке),
 * кто из КЦ сегодня на линии и сколько кому выдать до ровного уровня, на ком
 * сколько, без телефона; выдать N сотруднику, снять с сотрудника. Ниже —
 * «Повторные прозвоны в работе» (`docs/specs/2026-09-30-lead-redial-design.md`).
 * Соответствие учёток — отдельный раздел `/bitrix-users` (старый `?tab=users`
 * ведёт туда). Спека `docs/specs/2026-09-24-lead-distribution-design.md`;
 * числа — те же, что у `prokat-runner leads-distribution`.
 */
import { useGetIdentity } from "@refinedev/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router";

import {
  type AssignResult,
  assignDeals,
  assignLeads,
  type DealAssignResult,
  type Distribution,
  loadDealDistribution,
  loadDistribution,
  redialDeals,
  redialLeads,
  type RedialDeal,
  type DealRepeat,
  repeatDeals,
  unassignDeals,
  unassignLeads,
} from "@/lib/distribution";
import { LeadLink } from "@/components/LeadLink";
import { shortTime } from "@/lib/leads";
import type { CurrentUser } from "@/lib/providers";

import { DealStatsBlocks, DialingLink } from "@/pages/DealStatsBlocks";
import { DealsInwork } from "@/pages/DealsInwork";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
const input = "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg";
const button =
  "h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[12.5px] hover:bg-bg-hover disabled:cursor-wait disabled:opacity-50";

function resultText(r: AssignResult): string {
  const parts = [`изменено ${r.changed} из ${r.requested}`];
  if (r.found < r.requested) parts.push(`нашлось только ${r.found}`);
  if (r.errors.length) parts.push(`ошибок ${r.errors.length}: ${r.errors[0]?.error}`);
  return parts.join(", ");
}

type Give = (count: number, crm_user_id: number) => Promise<AssignResult>;

function AssignForm({
  d,
  max,
  give = assignLeads,
  onDone,
}: {
  d: Distribution;
  max: number;
  give?: Give;
  onDone: (text: string) => void;
}) {
  const [count, setCount] = useState(25);
  const [user, setUser] = useState("");
  const m = useMutation({
    mutationFn: () => give(count, Number(user)),
    onSuccess: (r) => onDone(`Выдано: ${resultText(r)}`),
    onError: (e: Error) => onDone(`Не вышло: ${e.message}`),
  });
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (user && count > 0) m.mutate();
      }}
      data-annot="распределить"
    >
      <span className="text-[12.5px] text-fg-muted">Распределить</span>
      <input
        type="number"
        min={1}
        max={Math.max(1, Math.min(max, 200))}
        value={count}
        onChange={(e) => setCount(Number(e.target.value))}
        className={`${input} w-20 tabular-nums`}
        aria-label="Сколько лидов"
      />
      <span className="text-[12.5px] text-fg-muted">лидов →</span>
      <select value={user} onChange={(e) => setUser(e.target.value)} className={`${input} min-w-56`} aria-label="Кому">
        <option value="">сотрудник…</option>
        {d.users.map((u) => (
          <option key={u.crm_user_id} value={u.crm_user_id}>
            {u.name}
          </option>
        ))}
      </select>
      <button type="submit" disabled={!user || count < 1 || max === 0 || m.isPending} className={button}>
        {m.isPending ? "Выдаю…" : "Выдать"}
      </button>
    </form>
  );
}

function OnLineBlock({
  d,
  redial = false,
  onDone,
}: {
  d: Distribution;
  redial?: boolean;
  onDone: (text: string) => void;
}) {
  const fn: Give = redial ? redialLeads : assignLeads;
  const m = useMutation({
    mutationFn: ({ count, crm }: { count: number; crm: number; name: string }) => fn(count, crm),
    onSuccess: (r, v) => onDone(`Выдано ${v.name}: ${resultText(r)}`),
    onError: (e: Error) => onDone(`Не вышло: ${e.message}`),
  });
  const amount = (u: Distribution["on_line"][number]) => (redial ? u.redial_give : u.give);
  const give = d.on_line.reduce((s, u) => s + amount(u), 0);
  return (
    <div className="w-full border-t border-border pt-3" data-annot={redial ? "повторные на линии" : "на линии сегодня"}>
      <div className="mb-1 flex flex-wrap items-baseline gap-x-3">
        <span className="text-[12.5px] font-medium">На линии сегодня: {d.on_line.length}</span>
        <span className="text-[11.5px] text-fg-faint">
          ставили статус «на линии» · выдаём до ровного уровня, у кого больше — не добавляем
        </span>
      </div>
      {d.on_line.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">Сегодня из КЦ на линию ещё никто не выходил.</div>
      ) : (
        <table className="text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className="py-1 pr-6 font-medium">Сотрудник</th>
              <th className="py-1 pr-6 text-right font-medium">Сейчас</th>
              <th className="py-1 pr-6 text-right font-medium">Выдать</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {d.on_line.map((u) => (
              <tr
                key={u.crm_user_id}
                className="border-t border-border"
                data-annot={`${redial ? "повторные" : "на линии"} ${u.crm_user_id}`}
              >
                <td className="py-1 pr-6">
                  {u.name}
                  {u.bitrix_user_id === null && <span className="ml-2 text-[11px] text-warn">нет учётки Bitrix</span>}
                </td>
                <td className="py-1 pr-6 text-right tabular-nums">{u.leads}</td>
                <td className="py-1 pr-6 text-right font-medium tabular-nums">{amount(u)}</td>
                <td className="py-1">
                  {amount(u) > 0 && (
                    <button
                      type="button"
                      className={button}
                      disabled={m.isPending}
                      onClick={() => m.mutate({ count: amount(u), crm: u.crm_user_id, name: u.name })}
                    >
                      Выдать {amount(u)}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border text-fg-muted">
              <td className="py-1 pr-6">Итого</td>
              <td className="py-1 pr-6 text-right tabular-nums">{d.on_line.reduce((s, u) => s + u.leads, 0)}</td>
              <td className="py-1 pr-6 text-right tabular-nums">{give}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}

function ByUserBlock({ d, onDone }: { d: Distribution; onDone: (text: string) => void }) {
  const [ask, setAsk] = useState<number | null>(null);
  const m = useMutation({
    mutationFn: (id: number) => unassignLeads(id),
    onSuccess: (r) => onDone(`Снято: ${resultText(r)}`),
    onError: (e: Error) => onDone(`Не вышло: ${e.message}`),
    onSettled: () => setAsk(null),
  });
  return (
    <section className={card} data-annot="с телефоном по сотрудникам">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">С телефоном — по сотрудникам: {d.assigned}</h2>
        <span className="text-[12px] text-fg-faint">крестик возвращает лиды сотрудника в «не распределено»</span>
      </div>
      {d.by_user.length === 0 ? (
        <div className="p-4 text-[12.5px] text-fg-muted">Распределённых лидов нет.</div>
      ) : (
        <table className="w-full text-[12.5px]">
          <tbody>
            {d.by_user.map((u) => (
              <tr key={u.bitrix_user_id} className="border-t border-border first:border-t-0 hover:bg-bg-hover" data-annot={`ответственный ${u.bitrix_user_id}`}>
                <td className="px-4 py-1.5">
                  {u.name}
                  {u.crm_user_id === null && <span className="ml-2 text-[11px] text-warn">нет связи с CRM</span>}
                  {u.active === false && <span className="ml-2 text-[11px] text-warn">уволен в Bitrix</span>}
                </td>
                <td className="px-4 py-1.5 font-mono text-[11px] text-fg-faint">bx {u.bitrix_user_id}</td>
                <td className="w-16 px-4 py-1.5 text-right tabular-nums">{u.leads}</td>
                <td className="w-72 px-4 py-1.5 text-right">
                  {ask === u.bitrix_user_id ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="text-fg-muted">Снять {u.leads}?</span>
                      <button type="button" className={button} disabled={m.isPending} onClick={() => m.mutate(u.bitrix_user_id)}>
                        {m.isPending ? "Снимаю…" : "Да"}
                      </button>
                      <button type="button" className={button} disabled={m.isPending} onClick={() => setAsk(null)}>
                        Нет
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      title="Снять лиды с сотрудника"
                      aria-label={`Снять лиды: ${u.name}`}
                      onClick={() => setAsk(u.bitrix_user_id)}
                      className="rounded p-1 text-fg-faint hover:bg-bg-hover hover:text-err"
                    >
                      <X size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function RedialLeadsBlock({ d, onDone }: { d: Distribution; onDone: (text: string) => void }) {
  const [open, setOpen] = useState(false);
  const r = d.redial;
  return (
    <section className={`${card} flex flex-wrap items-center gap-x-6 gap-y-3 p-4`} data-annot="повторные прозвоны">
      <div>
        <div className="text-[12px] text-fg-muted">Повторные прозвоны в работе</div>
        <div className="text-[26px] font-semibold leading-none tabular-nums">{r.leads.length}</div>
        <div className="mt-1 text-[11.5px] text-fg-faint">
          «В работе» за {r.days} дн., звонили больше {r.hours} ч назад или не звонили, за сегодня меньше {r.per_day} звонков · выдача переводит в «Новая»
        </div>
      </div>
      <AssignForm d={d} max={r.leads.length} give={redialLeads} onDone={onDone} />
      {r.leads.length > 0 && (
        <button type="button" className="ml-auto text-[12px] text-fg-muted hover:underline" onClick={() => setOpen(!open)}>
          {open ? "скрыть" : "список"}
        </button>
      )}
      <OnLineBlock d={d} redial onDone={onDone} />
      {open && (
        <div className="max-h-[420px] w-full overflow-auto border-t border-border pt-2">
          <table className="w-full text-[12.5px]">
            <thead className="sticky top-0 bg-bg-subtle text-left text-[11.5px] text-fg-faint">
              <tr>
                {["Лид", "Создан", "Последний звонок", "Ответственный"].map((h) => (
                  <th key={h} className="whitespace-nowrap py-1 pr-6 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {r.leads.map((x) => (
                <tr key={x.lead_id} className="border-t border-border hover:bg-bg-hover" data-annot={`повторный ${x.lead_id}`}>
                  <td className="py-1 pr-6 font-mono tabular-nums">
                    <LeadLink id={x.lead_id} />
                  </td>
                  <td className="whitespace-nowrap py-1 pr-6 tabular-nums text-fg-muted">{shortTime(x.created)}</td>
                  <td className="whitespace-nowrap py-1 pr-6 tabular-nums">
                    {x.last_call ? shortTime(x.last_call) : <span className="text-fg-faint">не звонили</span>}
                  </td>
                  <td className="py-1 pr-6">{x.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function NoPhoneBlock({ d }: { d: Distribution }) {
  return (
    <section className={card} data-annot="без российского номера">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">Без российского номера: {d.no_phone.length}</h2>
        <span className="text-[12px] text-fg-faint">нет номера или номер не российский — не распределяем</span>
      </div>
      {d.no_phone.length > 0 && (
        <div className="max-h-[420px] overflow-auto">
          <table className="w-full text-[12.5px]">
            <thead className="sticky top-0 bg-bg-subtle text-left text-[11.5px] text-fg-faint">
              <tr>
                {["Лид", "Создан", "Номер", "Ответственный", "Название"].map((h) => (
                  <th key={h} className="whitespace-nowrap px-4 py-2 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.no_phone.map((x) => (
                <tr key={x.lead_id} className="border-t border-border hover:bg-bg-hover" data-annot={`лид ${x.lead_id}`}>
                  <td className="px-4 py-1.5 font-mono tabular-nums">
                    <LeadLink id={x.lead_id} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums text-fg-muted">{shortTime(x.created)}</td>
                  <td className="whitespace-nowrap px-4 py-1.5 font-mono tabular-nums text-fg-muted">{x.phone || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-1.5">{x.name}</td>
                  <td className="px-4 py-1.5">{x.title}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function LeadsTab() {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const q = useQuery({ queryKey: ["distribution"], queryFn: loadDistribution, refetchInterval: 60_000 });
  const done = (text: string) => {
    setNote(text);
    void qc.invalidateQueries({ queryKey: ["distribution"] });
  };
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">Не удалось получить распределение: {q.error.message}</div>;
  const d = q.data;
  return (
    <>
      <section className={`${card} flex flex-wrap items-center gap-x-6 gap-y-3 p-4`} data-annot="не распределено">
        <div>
          <div className="text-[12px] text-fg-muted">Не распределено</div>
          <div className="text-[26px] font-semibold leading-none tabular-nums">{d.unassigned}</div>
          <div className="mt-1 text-[11.5px] text-fg-faint">
            NEW с телефоном на системной учётке · синхр. {shortTime(d.last_sync)}
          </div>
        </div>
        <AssignForm d={d} max={d.unassigned} onDone={done} />
        <OnLineBlock d={d} onDone={done} />
      </section>
      {note && <div className="text-[12.5px] text-fg-muted">{note}</div>}
      <RedialLeadsBlock d={d} onDone={done} />
      <ByUserBlock d={d} onDone={done} />
      <NoPhoneBlock d={d} />
    </>
  );
}

function dealResultText(r: DealAssignResult): string {
  const parts = [`изменено ${r.changed} из ${r.requested}`];
  if (r.requested === 0 && r.held !== undefined) return `уже ${r.held} — добирать нечего`;
  if (r.found < r.requested) parts.push(`свободных нашлось ${r.found}`);
  if (r.by_type && Object.keys(r.by_type).length)
    parts.push(Object.entries(r.by_type).map(([k, v]) => `${k} ${v}`).join(", "));
  if (r.errors.length) parts.push(`ошибок ${r.errors.length}: ${r.errors[0]?.error}`);
  return parts.join("; ");
}

function DealsTab() {
  const qc = useQueryClient();
  const { data: me } = useGetIdentity<CurrentUser>();
  const [note, setNote] = useState("");
  const [ask, setAsk] = useState<number | null>(null);
  const [stage, setStage] = useState("3");
  const q = useQuery({ queryKey: ["deal-distribution"], queryFn: loadDealDistribution, refetchInterval: 60_000 });
  const done = (text: string) => {
    setNote(text);
    void qc.invalidateQueries({ queryKey: ["deal-distribution"] });
  };
  const give = useMutation({
    mutationFn: ({ crm }: { crm: number; name: string }) => assignDeals(crm),
    onSuccess: (r, v) => done(`Выдано ${v.name}: ${dealResultText(r)}`),
    onError: (e: Error) => done(`Не вышло: ${e.message}`),
  });
  const redial = useMutation({
    mutationFn: ({ crm }: { crm: number; name: string }) => redialDeals(crm),
    onSuccess: (r, v) => done(`На перезвон ${v.name}: ${dealResultText(r)}`),
    onError: (e: Error) => done(`Не вышло: ${e.message}`),
  });
  const again = useMutation({
    mutationFn: ({ crm }: { crm: number; name: string }) => repeatDeals(crm, stage),
    onSuccess: (r, v) => done(`Будущий перезвон «${stageName}» ${v.name}: ${dealResultText(r)}`),
    onError: (e: Error) => done(`Не вышло: ${e.message}`),
  });
  const take = useMutation({
    mutationFn: ({ crm }: { crm: number; name: string }) => unassignDeals(crm),
    onSuccess: (r, v) => done(`Снято с ${v.name}: ${dealResultText(r)}`),
    onError: (e: Error) => done(`Не вышло: ${e.message}`),
    onSettled: () => setAsk(null),
  });
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Считаю обзвон по CRM…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">Не удалось получить распределение: {q.error.message}</div>;
  const d = q.data;
  const busy = give.isPending || take.isPending || redial.isPending || again.isPending;
  const stageName = d.repeat.stages.find((x) => x.id === stage)?.name ?? "";
  const mine = me ? d.on_line.find((u) => u.crm_user_id === me.id) : undefined;
  return (
    <>
      <section className={`${card} flex flex-wrap items-start gap-x-8 gap-y-3 p-4`} data-annot="сделки к выдаче">
        <div>
          <div className="text-[12px] text-fg-muted">К выдаче</div>
          <div className="text-[26px] font-semibold leading-none tabular-nums">{d.unassigned}</div>
          <div className="mt-1 text-[11.5px] text-fg-faint">сделки обзвона без ответственного · {shortTime(d.now)}</div>
        </div>
        <table className="text-[12.5px]" data-annot="очередь выдачи">
          <tbody>
            {d.queue.map((t, i) => (
              <tr key={t.type_id}>
                <td className="pr-3 text-fg-faint tabular-nums">{i + 1}</td>
                <td className="pr-4">{t.name}</td>
                <td className="text-right tabular-nums">{t.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {me && (
          <div className="flex flex-col gap-1" data-annot="взять себе">
            <button
              type="button"
              className={button}
              disabled={busy || (mine !== undefined && mine.give === 0)}
              onClick={() => give.mutate({ crm: me.id, name: me.name })}
            >
              {give.isPending ? "Выдаю…" : `Взять себе до ${d.limit}`}
            </button>
            <span className="text-[11.5px] text-fg-faint">
              {mine ? `на вас ${mine.held}, добавится ${mine.give}` : "вас нет среди вышедших на линию"}
            </span>
          </div>
        )}
        <div className="w-full border-t border-border pt-3" data-annot="сделки на линии сегодня">
          <div className="mb-1 flex flex-wrap items-baseline gap-x-3">
            <span className="text-[12.5px] font-medium">На линии сегодня: {d.on_line.length}</span>
            <span className="text-[11.5px] text-fg-faint">
              добираем до {d.limit}: сначала «В работе», потом «Думают», «Ждём клиента», «Новая», «Нет оплаты», «Товар на
              выбор» · CRM сама снимает сделку после звонка, смены стадии и ночью
            </span>
          </div>
          {d.on_line.length === 0 ? (
            <div className="text-[12.5px] text-fg-muted">Сегодня из КЦ на линию ещё никто не выходил.</div>
          ) : (
            <table className="w-full text-[12.5px]">
              <thead className="text-left text-[11.5px] text-fg-faint">
                <tr>
                  <th className="py-1 pr-4 font-medium">Сотрудник</th>
                  <th className="px-4 py-1 text-right font-medium">На нём</th>
                  <th className="px-4 py-1 text-right font-medium">Из них в обзвоне</th>
                  <th className="px-4 py-1 text-right font-medium">Добавится</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {d.on_line.map((u) => (
                  <tr key={u.crm_user_id} className="border-t border-border hover:bg-bg-hover" data-annot={`сделки ${u.crm_user_id}`}>
                    <td className="py-1.5 pr-4">{u.name}</td>
                    <td className="px-4 py-1.5 text-right tabular-nums">{u.held}</td>
                    <td className="px-4 py-1.5 text-right tabular-nums text-fg-muted">
                      <DialingLink n={u.in_call_list} user={u.crm_user_id} />
                    </td>
                    <td className="px-4 py-1.5 text-right font-medium tabular-nums">{u.give}</td>
                    <td className="w-96 py-1.5 pl-4 text-right">
                      {ask === u.crm_user_id ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="text-fg-muted">Снять {u.held}?</span>
                          <button type="button" className={button} disabled={busy} onClick={() => take.mutate({ crm: u.crm_user_id, name: u.name })}>
                            {take.isPending ? "Снимаю…" : "Да"}
                          </button>
                          <button type="button" className={button} disabled={busy} onClick={() => setAsk(null)}>
                            Нет
                          </button>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          {u.give > 0 && (
                            <button type="button" className={button} disabled={busy} onClick={() => give.mutate({ crm: u.crm_user_id, name: u.name })}>
                              Добрать {u.give}
                            </button>
                          )}
                          {u.redial_give > 0 && (
                            <button type="button" className={button} disabled={busy} onClick={() => redial.mutate({ crm: u.crm_user_id, name: u.name })}>
                              Перезвон {u.redial_give}
                            </button>
                          )}
                          {(u.repeat_give[stage] ?? 0) > 0 && (
                            <button
                              type="button"
                              className={button}
                              disabled={busy}
                              title={`Будущий перезвон: «${stageName}»`}
                              onClick={() => again.mutate({ crm: u.crm_user_id, name: u.name })}
                            >
                              Будущий перезвон {u.repeat_give[stage]}
                            </button>
                          )}
                          {u.held > 0 && (
                            <button
                              type="button"
                              title="Снять сделки с сотрудника"
                              aria-label={`Снять сделки: ${u.name}`}
                              onClick={() => setAsk(u.crm_user_id)}
                              className="rounded p-1 text-fg-faint hover:bg-bg-hover hover:text-err"
                            >
                              <X size={14} />
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
      {note && <div className="text-[12.5px] text-fg-muted">{note}</div>}
      <RedialBlock deals={d.redial} />
      <RepeatBlock r={d.repeat} stage={stage} onStage={setStage} />
      <DealStatsBlocks />
    </>
  );
}

function RedialBlock({ deals }: { deals: RedialDeal[] }) {
  const [open, setOpen] = useState(false);
  const byType = deals.reduce<Record<string, number>>((m, r) => ({ ...m, [r.type]: (m[r.type] ?? 0) + 1 }), {});
  return (
    <section className={card} data-annot="перезвон">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">Перезвон: {deals.length}</h2>
        <span className="text-[12px] text-fg-faint">
          утром были в обзвоне, сегодня не дозвонились, с последнего звонка больше часа, попыток меньше четырёх ·
          кнопка «Перезвон» у сотрудника добирает до лимита
        </span>
        {deals.length > 0 && (
          <button type="button" className="ml-auto text-[12px] text-fg-muted hover:underline" onClick={() => setOpen(!open)}>
            {open ? "скрыть" : "список"}
          </button>
        )}
      </div>
      {deals.length === 0 ? (
        <div className="p-4 text-[12.5px] text-fg-muted">Перезванивать некому.</div>
      ) : (
        <div className="px-4 py-2 text-[12.5px] text-fg-muted">
          {Object.entries(byType).map(([k, v]) => `${k} ${v}`).join(" · ")}
        </div>
      )}
      {open && deals.length > 0 && (
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-1 font-medium">Сделка</th>
              <th className="px-4 py-1 font-medium">Тип</th>
              <th className="px-4 py-1 font-medium">Последний звонок</th>
              <th className="px-4 py-1 font-medium">У клиента</th>
            </tr>
          </thead>
          <tbody>
            {deals.map((r) => (
              <tr key={r.id} className="border-t border-border">
                <td className="px-4 py-1.5">
                  <Link to={`/deals/${r.id}`} className="underline">{r.id}</Link>
                </td>
                <td className="px-4 py-1.5">{r.type}</td>
                <td className="px-4 py-1.5 tabular-nums">{shortTime(r.last_call_at)}</td>
                <td className={`px-4 py-1.5 tabular-nums ${r.callable_now ? "" : "text-fg-faint"}`}>
                  {r.local_time}{r.callable_now ? "" : " · вне часов"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** «Будущий перезвон»: `docs/specs/2026-09-25-deal-distribution-design.md`. Стадия
 * переключателя решает, из какой раздаёт кнопка «Будущий перезвон» у сотрудника. */
function RepeatBlock({ r, stage, onStage }: { r: DealRepeat; stage: string; onStage: (s: string) => void }) {
  const [open, setOpen] = useState(false);
  const deals = r.deals.filter((x) => x.stage_id === stage);
  const total = r.stages.find((x) => x.id === stage)?.count ?? 0;
  const never = deals.filter((x) => !x.last_call_at).length;
  const byDay = deals.reduce<Record<string, number>>((m, x) => {
    if (!x.last_call_at) return m;
    const k = x.last_call_at.slice(0, 10);
    return { ...m, [k]: (m[k] ?? 0) + 1 };
  }, {});
  return (
    <section className={card} data-annot="будущий перезвон">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">Будущий перезвон: {total}</h2>
        <div role="tablist" aria-label="Стадия" className="flex gap-1" data-annot="стадия повтора">
          {r.stages.map((x) => (
            <button
              key={x.id}
              type="button"
              role="tab"
              aria-selected={stage === x.id}
              onClick={() => onStage(x.id)}
              className={`rounded-[var(--radius-ui)] px-2 py-0.5 text-[12.5px] ${
                stage === x.id ? "bg-bg-hover font-medium text-fg" : "text-fg-muted hover:text-fg"
              }`}
            >
              {x.name} {x.count}
            </button>
          ))}
        </div>
        <span className="text-[12px] text-fg-faint">
          сделки, созданные за {r.days} дн.: не звонили вовсе или последний звонок был до сегодня и без дозвона, попыток
          за неделю меньше {r.attempts}; ни на ком, не в сегодняшнем обзвоне — дату обработки не смотрим · выдаёт кнопка
          «Будущий перезвон» в строке сотрудника выше — из выбранной здесь стадии, до лимита: сначала не звонили, потом
          свежие недозвоны
          {r.unknown > 0 && ` · без контактов в копии ${r.unknown} — не берём`}
        </span>
        {deals.length > 0 && (
          <button type="button" className="ml-auto text-[12px] text-fg-muted hover:underline" onClick={() => setOpen(!open)}>
            {open ? "скрыть" : "список"}
          </button>
        )}
      </div>
      {deals.length === 0 ? (
        <div className="p-4 text-[12.5px] text-fg-muted">Повторять некого.</div>
      ) : (
        <div className="px-4 py-2 text-[12.5px] text-fg-muted" data-annot="повтор по дням">
          не звонили — <span className="font-medium text-fg">{never}</span> · последний звонок:{" "}
          {Object.entries(byDay)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([k, v]) => `${k.slice(8, 10)}.${k.slice(5, 7)} — ${v}`)
            .join(" · ")}
        </div>
      )}
      {open && deals.length > 0 && (
        <div className="max-h-[420px] overflow-auto">
          <table className="w-full text-[12.5px]">
            <thead className="sticky top-0 bg-bg-subtle text-left text-[11.5px] text-fg-faint">
              <tr>
                <th className="px-4 py-1 font-medium">Сделка</th>
                <th className="px-4 py-1 font-medium">Создана</th>
                <th className="px-4 py-1 font-medium">Последний звонок</th>
                <th className="px-4 py-1 text-right font-medium">Попыток за {r.days} дн.</th>
                <th className="px-4 py-1 font-medium">След. обработка</th>
                <th className="px-4 py-1 font-medium">У клиента</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((x) => (
                <tr key={x.id} className="border-t border-border hover:bg-bg-hover" data-annot={`повтор ${x.id}`}>
                  <td className="px-4 py-1.5">
                    <Link to={`/deals/${x.id}`} className="underline">{x.id}</Link>
                  </td>
                  <td className="px-4 py-1.5 tabular-nums text-fg-muted">{shortTime(x.created_at)}</td>
                  <td className="px-4 py-1.5 tabular-nums">
                    {x.last_call_at ? shortTime(x.last_call_at) : <span className="text-fg-faint">не звонили</span>}
                  </td>
                  <td className="px-4 py-1.5 text-right tabular-nums">{x.attempts}</td>
                  <td className="px-4 py-1.5 tabular-nums text-fg-muted">{x.wait_at ? shortTime(x.wait_at) : "—"}</td>
                  <td className={`px-4 py-1.5 tabular-nums ${x.callable_now ? "" : "text-fg-faint"}`}>
                    {x.local_time}{x.callable_now ? "" : " · вне часов"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const DEAL_TABS = [
  { id: "call", label: "Обзвон" },
  { id: "inwork", label: "Сделки в работе" },
] as const;

/** Вкладки внутри «Сделок»: `?tab=inwork` — «Сделки в работе» (`DealsInwork`). */
function DealsTabs() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "inwork" ? "inwork" : "call";
  const select = (id: (typeof DEAL_TABS)[number]["id"]) => {
    const out = new URLSearchParams(params);
    if (id === "call") out.delete("tab");
    else out.set("tab", id);
    setParams(out, { replace: true });
  };
  return (
    <>
      <div role="tablist" aria-label="Какие сделки" className="flex gap-1" data-annot="вкладки сделок">
        {DEAL_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => select(t.id)}
            className={`rounded-[var(--radius-ui)] px-3 py-1 text-[12.5px] ${
              tab === t.id ? "bg-bg-hover font-medium text-fg" : "text-fg-muted hover:text-fg"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "inwork" ? <DealsInwork /> : <DealsTab />}
    </>
  );
}

const KINDS = [
  { id: "leads", label: "Лиды" },
  { id: "deals", label: "Сделки" },
] as const;
type Kind = (typeof KINDS)[number]["id"];

export function DistributionPage() {
  const [params, setParams] = useSearchParams();
  if (params.get("tab") === "users") return <Navigate to="/bitrix-users" replace />;
  const kind: Kind = params.get("kind") === "deals" ? "deals" : "leads";
  const select = (id: Kind) => {
    const out = new URLSearchParams(params);
    if (id === "leads") out.delete("kind");
    else out.set("kind", id);
    out.delete("tab");
    setParams(out, { replace: true });
  };
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 [&>*]:flex-none">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-2 border-b border-border" data-annot="шапка распределения">
        <h1 className="pb-2 text-[16px] font-semibold tracking-[-0.015em]">Распределение</h1>
        <div role="tablist" aria-label="Что распределяем" className="flex gap-1">
          {KINDS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={kind === t.id}
              onClick={() => select(t.id)}
              className={`-mb-px border-b-2 px-3 pb-2 pt-1 text-[13px] ${
                kind === t.id ? "border-accent font-medium text-fg" : "border-transparent text-fg-muted hover:text-fg"
              }`}
              data-annot={`вкладка «${t.label}»`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {kind === "leads" ? <LeadsTab /> : <DealsTabs />}
    </main>
  );
}
